import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import api from '../services/api';

export interface Subscription {
  id: number;
  user_id: number;
  plan: 'basic' | 'pro' | 'premium';
  status: 'incomplete' | 'incomplete_expired' | 'trialing' | 'active' | 'past_due' | 'canceled' | 'unpaid' | 'paused';
  stripe_subscription_id: string | null;
  has_active_subscription?: boolean;
  current_period_start: string;
  current_period_end: string;
  cancel_at_period_end?: boolean;
  applications_this_week: number;
  applications_limit: number;
  created_at: string;
}

export interface SubscriptionState {
  subscription: Subscription | null;
  loading: boolean;
  error: string | null;
  checkoutUrl: string | null;
}

const initialState: SubscriptionState = {
  subscription: null,
  loading: false,
  error: null,
  checkoutUrl: null,
};

function normalizeSubscriptionPayload(data: any): Subscription | null {
  if (!data || data.status === 'none' || !data.plan) return null;

  const usedValue = data.applications_this_week ?? data.applications_used_this_week ?? 0;
  const storedLimit = data.applications_limit ?? data.applications_limit_per_week;
  const used = Number(usedValue);
  const limit = Number(storedLimit);
  const hasActiveSubscription = data.status === 'active'
    && data.has_active_subscription === true
    && /^sub_[A-Za-z0-9]+$/.test(data.stripe_subscription_id || '');
  let applicationsLimit = hasActiveSubscription && Number.isFinite(limit) ? limit : 0;
  if (hasActiveSubscription && (data.plan === 'pro' || data.plan === 'premium')) {
    applicationsLimit = -1;
  } else if (hasActiveSubscription && data.plan === 'basic' && !Number.isFinite(limit)) {
    applicationsLimit = 20;
  }

  return {
    ...data,
    has_active_subscription: hasActiveSubscription,
    applications_this_week: Number.isFinite(used) ? used : 0,
    applications_limit: applicationsLimit,
  };
}

export const fetchSubscription = createAsyncThunk(
  'subscription/fetchSubscription',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/subscriptions/current');
      return normalizeSubscriptionPayload(response.data);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 404) {
          return null;
        }
        return rejectWithValue(error.response?.data?.message || 'Failed to fetch subscription');
      }
      return rejectWithValue('Failed to fetch subscription');
    }
  }
);

export const createSubscription = createAsyncThunk(
  'subscription/createSubscription',
  async (plan: 'basic', { rejectWithValue }) => {
    try {
      const response = await api.post('/subscriptions/create', { plan });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data?.message || 'Failed to create subscription');
      }
      return rejectWithValue('Failed to create subscription');
    }
  }
);

export const openBillingPortal = createAsyncThunk(
  'subscription/openBillingPortal',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.post('/subscriptions/billing-portal');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data?.message || 'Failed to open billing portal');
      }
      return rejectWithValue('Failed to open billing portal');
    }
  }
);

export const cancelSubscription = createAsyncThunk(
  'subscription/cancelSubscription',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.post('/subscriptions/cancel');
      return normalizeSubscriptionPayload(
        response.data?.subscription ?? response.data,
      );
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data?.message || 'Failed to cancel subscription');
      }
      return rejectWithValue('Failed to cancel subscription');
    }
  }
);

const subscriptionSlice = createSlice({
  name: 'subscription',
  initialState,
  reducers: {
    clearCheckoutUrl: (state) => {
      state.checkoutUrl = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSubscription.fulfilled, (state, action) => {
        state.loading = false;
        state.subscription = action.payload;
      })
      .addCase(fetchSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSubscription.fulfilled, (state, action) => {
        state.loading = false;
        state.checkoutUrl = action.payload.checkoutUrl;
        if (action.payload.subscription) {
          state.subscription = action.payload.subscription;
        }
      })
      .addCase(createSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(cancelSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cancelSubscription.fulfilled, (state, action) => {
        state.loading = false;
        state.subscription = action.payload;
      })
      .addCase(cancelSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearCheckoutUrl } = subscriptionSlice.actions;
export default subscriptionSlice.reducer;
